import React, { useMemo } from "react";
import { Box, Card, CardContent, Typography, Paper, Switch, FormControlLabel, useTheme } from "@mui/material";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  DirectionsRenderer,
} from "@react-google-maps/api";
import { Invoice, LogisticInvoice } from "../../QueueBoard/models/logisticsTypes";

type Props = {
  isLoaded: boolean;
  loadError: any;
  mapRef: any;
  containerStyle: any;
  center: { lat: number; lng: number };
  workOrders: Invoice[];
  selectedWorkOrders: number[];
  selectedWorkOrdersOrder: number[];
  selectedWorkOrdersData: Map<number, LogisticInvoice>;
  geocodedAddresses: Map<number, { lat: number; lng: number }>;
  directionsResponse: any;
  optimizedDirectionsResponse?: google.maps.DirectionsResult | null;
  optimizedRouteOrder?: number[]; // Work order IDs in optimized sequence
  showOptimizedRoute?: boolean;
  onToggleOptimizedRoute?: () => void;
  placeDetails: any;
  selectedPlace: any;
  onMarkerClick: (wo: Invoice, pos: { lat: number; lng: number }) => void;
  onCloseInfo: () => void;
  onDirectionsChanged?: (directions: any) => void;
  directionsRendererRef?: React.MutableRefObject<google.maps.DirectionsRenderer | null>;
  startingPoint?: string;
  startingPointCoordinates?: { lat: number; lng: number } | null;
  addresses?: Map<number, string>;
};

// Helper function to format duration in seconds to readable format
const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

// Helper function to calculate total duration from directions response
const calculateTotalDuration = (directionsResponse: google.maps.DirectionsResult | null): number => {
  if (!directionsResponse?.routes?.[0]?.legs) return 0;
  return directionsResponse.routes[0].legs.reduce((total, leg) => {
    return total + (leg.duration?.value || 0);
  }, 0);
};

// Helper function to calculate total distance from directions response
const calculateTotalDistance = (directionsResponse: google.maps.DirectionsResult | null): number => {
  if (!directionsResponse?.routes?.[0]?.legs) return 0;
  return directionsResponse.routes[0].legs.reduce((total, leg) => {
    return total + (leg.distance?.value || 0);
  }, 0);
};

// Format distance in meters to readable format (shows both km and miles)
const formatDistance = (meters: number): string => {
  const km = meters / 1000;
  const miles = km * 0.621371;
  if (km >= 1) {
    return `${km.toFixed(1)} km (${miles.toFixed(1)} mi)`;
  }
  return `${meters} m`;
};

const LogisticsMapPanel: React.FC<Props> = ({
  isLoaded,
  loadError,
  mapRef,
  containerStyle,
  center,
  workOrders,
  selectedWorkOrders,
  selectedWorkOrdersOrder,
  selectedWorkOrdersData,
  geocodedAddresses,
  directionsResponse,
  optimizedDirectionsResponse,
  optimizedRouteOrder = [],
  showOptimizedRoute = true,
  onToggleOptimizedRoute,
  placeDetails,
  selectedPlace,
  onMarkerClick,
  onCloseInfo,
  onDirectionsChanged,
  directionsRendererRef,
  startingPoint,
  startingPointCoordinates,
  addresses,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Calculate route times
  const userRouteDuration = useMemo(() => calculateTotalDuration(directionsResponse), [directionsResponse]);
  const userRouteDistance = useMemo(() => calculateTotalDistance(directionsResponse), [directionsResponse]);
  const optimizedRouteDuration = useMemo(() => calculateTotalDuration(optimizedDirectionsResponse || null), [optimizedDirectionsResponse]);
  const optimizedRouteDistance = useMemo(() => calculateTotalDistance(optimizedDirectionsResponse || null), [optimizedDirectionsResponse]);
  
  // Calculate time savings (positive means optimized route is faster)
  const timeSavings = userRouteDuration - optimizedRouteDuration;

  // Build optimized markers data using the optimizedRouteOrder (work order IDs in optimized sequence)
  const optimizedMarkersData = useMemo(() => {
    if (!optimizedRouteOrder || optimizedRouteOrder.length < 2 || !showOptimizedRoute) return [];
    
    const hasStartingPoint = startingPoint && startingPoint.trim();
    
    // Create markers in optimized order
    const markers: Array<{
      id: number;
      coordinates: { lat: number; lng: number };
      stopNumber: number;
      documentNumber: string;
      customerName: string;
    }> = [];
    
    // optimizedRouteOrder contains work order IDs in optimized sequence
    optimizedRouteOrder.forEach((workOrderId, optimizedIndex) => {
      const wo = selectedWorkOrdersData.get(workOrderId);
      if (wo) {
        const coords = geocodedAddresses.get(workOrderId);
        if (coords) {
          markers.push({
            id: workOrderId,
            coordinates: coords,
            // Stop number: +2 if starting point exists (starting point is 1), +1 otherwise
            stopNumber: hasStartingPoint ? optimizedIndex + 2 : optimizedIndex + 1,
            documentNumber: wo.documentNumber || '',
            customerName: wo.customerName || '',
          });
        }
      }
    });
    
    return markers;
  }, [optimizedRouteOrder, showOptimizedRoute, selectedWorkOrdersData, geocodedAddresses, startingPoint]);
  return (
    <Card sx={{ height: "100%", position: "relative" }}>
      <CardContent sx={{ height: "100%", p: 0, pb: "2px", "&:last-child": { pb: "2px" } }}>
        {loadError ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              flexDirection: "column",
            }}
          >
            <Typography variant="h6" color="error" sx={{ mb: 1 }}>
              Error loading Google Maps
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {loadError.message}
            </Typography>
          </Box>
        ) : isLoaded ? (
          <>
            <GoogleMap
              onLoad={(map) => {
                mapRef.current = map;
              }}
              mapContainerStyle={containerStyle}
              center={center}
              zoom={10}
            >
              {/* Starting point marker (if exists) */}
              {startingPoint &&
                startingPoint.trim() &&
                selectedWorkOrdersOrder.length > 0 && (
                  <Marker
                    key="starting-point"
                    position={startingPointCoordinates || center}
                    title={`Starting Point: ${startingPoint}`}
                    label={{
                      text: "1",
                      color: "white",
                      fontWeight: "bold",
                    }}
                    icon={{
                      url:
                        "data:image/svg+xml;charset=UTF-8," +
                        encodeURIComponent(
                          '<svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">' +
                            '<circle cx="20" cy="20" r="18" fill="#4CAF50" stroke="#fff" stroke-width="2"/>' +
                            '<text x="20" y="26" text-anchor="middle" fill="white" font-size="14" font-weight="bold">1</text>' +
                            "</svg>"
                        ),
                      scaledSize: new google.maps.Size(40, 40),
                      anchor: new google.maps.Point(20, 20),
                    }}
                  />
                )}

              {/* User-selected route markers (blue) */}
              {selectedWorkOrdersOrder.map((workOrderId, index) => {
                const workOrder = selectedWorkOrdersData.get(workOrderId);
                const coordinates = geocodedAddresses.get(workOrderId);
                if (!workOrder || !coordinates) return null;

                const stopNumber =
                  startingPoint && startingPoint.trim() ? index + 2 : index + 1;

                return (
                  <Marker
                    key={`user-${workOrderId}`}
                    position={coordinates}
                    onClick={() => onMarkerClick(workOrder as any, coordinates)}
                    title={`User Route Stop ${stopNumber}: ${workOrder.documentNumber ?? ""} - ${
                      workOrder.customerName ?? ""
                    }`}
                    icon={{
                      url:
                        "data:image/svg+xml;charset=UTF-8," +
                        encodeURIComponent(
                          `<svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="20" cy="20" r="18" fill="#1976d2" stroke="#fff" stroke-width="2"/>
                            <text x="20" y="26" text-anchor="middle" fill="white" font-size="14" font-weight="bold">${stopNumber}</text>
                          </svg>`
                        ),
                      scaledSize: new google.maps.Size(40, 40),
                      anchor: new google.maps.Point(20, 20),
                    }}
                  />
                );
              })}

              {/* Optimized route markers (orange) - shown with offset to avoid overlap */}
              {showOptimizedRoute && optimizedMarkersData.map((marker) => (
                <Marker
                  key={`optimized-${marker.id}`}
                  position={{
                    lat: marker.coordinates.lat + 0.0003, // Small offset to show next to user marker
                    lng: marker.coordinates.lng + 0.0003,
                  }}
                  title={`Optimized Route Stop ${marker.stopNumber}: ${marker.documentNumber} - ${marker.customerName}`}
                  icon={{
                    url:
                      "data:image/svg+xml;charset=UTF-8," +
                      encodeURIComponent(
                        `<svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="16" cy="16" r="14" fill="#FF9800" stroke="#fff" stroke-width="2"/>
                          <text x="16" y="21" text-anchor="middle" fill="white" font-size="12" font-weight="bold">${marker.stopNumber}</text>
                        </svg>`
                      ),
                    scaledSize: new google.maps.Size(32, 32),
                    anchor: new google.maps.Point(16, 16),
                  }}
                />
              ))}

              {/* User-selected route polyline (blue) */}
              {directionsResponse && (
                <DirectionsRenderer
                  directions={directionsResponse}
                  options={{
                    draggable: false,
                    suppressMarkers: true,
                    suppressInfoWindows: true,
                    polylineOptions: {
                      strokeColor: "#1976d2",
                      strokeWeight: 5,
                      strokeOpacity: 0.8,
                    },
                  }}
                  onLoad={(renderer) => {
                    if (directionsRendererRef) {
                      directionsRendererRef.current = renderer;
                    }
                  }}
                  onDirectionsChanged={() => {}}
                />
              )}

              {/* Optimized route polyline (orange) */}
              {showOptimizedRoute && optimizedDirectionsResponse && (
                <DirectionsRenderer
                  directions={optimizedDirectionsResponse}
                  options={{
                    draggable: false,
                    suppressMarkers: true,
                    suppressInfoWindows: true,
                    polylineOptions: {
                      strokeColor: "#FF9800",
                      strokeWeight: 4,
                      strokeOpacity: 0.7,
                    },
                  }}
                />
              )}
            </GoogleMap>

            {/* Route Info & Legend Panel */}
            {selectedWorkOrdersOrder.length > 0 && (
              <Paper
                elevation={3}
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  p: 1.5,
                  minWidth: 220,
                  maxWidth: 280,
                  bgcolor: isDark
                    ? "rgba(30, 30, 30, 0.92)"
                    : "rgba(255, 255, 255, 0.95)",
                  color: theme.palette.text.primary,
                  zIndex: 1000,
                }}
              >
                {/* Route Times */}
                {userRouteDuration > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", mb: 0.5, color: isDark ? "#64b5f6" : "#1976d2" }}
                    >
                      Your Route (Round Trip)
                    </Typography>
                    <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: 0.5, color: theme.palette.text.primary }}>
                      <span style={{ color: isDark ? "#64b5f6" : "#1976d2" }}>●</span>
                      {formatDuration(userRouteDuration)} • {formatDistance(userRouteDistance)}
                    </Typography>
                  </Box>
                )}

                {showOptimizedRoute && optimizedRouteDuration > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", mb: 0.5, color: isDark ? "#ffb74d" : "#FF9800" }}
                    >
                      Optimized Route (Round Trip)
                    </Typography>
                    <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: 0.5, color: theme.palette.text.primary }}>
                      <span style={{ color: isDark ? "#ffb74d" : "#FF9800" }}>●</span>
                      {formatDuration(optimizedRouteDuration)} • {formatDistance(optimizedRouteDistance)}
                    </Typography>
                    {timeSavings > 0 && (
                      <Typography variant="caption" sx={{ color: "success.main", fontWeight: "bold" }}>
                        Saves {formatDuration(timeSavings)}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.3 }}>
                      (Nearest-neighbor optimization)
                    </Typography>
                  </Box>
                )}

                {/* Toggle for optimized route */}
                {optimizedDirectionsResponse && onToggleOptimizedRoute && (
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={showOptimizedRoute}
                        onChange={onToggleOptimizedRoute}
                        color="warning"
                      />
                    }
                    label={<Typography variant="caption">Show Optimized Route</Typography>}
                    sx={{ mb: 1 }}
                  />
                )}

                {/* Legend */}
                <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, pt: 1, mt: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: "bold", display: "block", mb: 0.5 }}>
                    Legend
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.3 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: "50%", bgcolor: "#4CAF50", border: `1px solid ${theme.palette.background.paper}` }} />
                    <Typography variant="caption">Starting Point</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.3 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: "50%", bgcolor: isDark ? "#64b5f6" : "#1976d2", border: `1px solid ${theme.palette.background.paper}` }} />
                    <Typography variant="caption">Your Route Stops</Typography>
                  </Box>
                  {showOptimizedRoute && optimizedDirectionsResponse && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Box sx={{ width: 14, height: 14, borderRadius: "50%", bgcolor: isDark ? "#ffb74d" : "#FF9800", border: `1px solid ${theme.palette.background.paper}` }} />
                      <Typography variant="caption">Optimized Route Stops</Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            )}
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography variant="h6">Loading map...</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

function areEqual(prev: Props, next: Props) {
  // Avoid re-rendering the map when only list filters change.
  // Re-render if any of these meaningful props change:
  // - Google load/error state, map refs, container/center
  if (prev.isLoaded !== next.isLoaded) return false;
  if (prev.loadError !== next.loadError) return false;
  if (prev.containerStyle !== next.containerStyle) return false;
  if (
    prev.center.lat !== next.center.lat ||
    prev.center.lng !== next.center.lng
  )
    return false;

  // - Selected work orders list
  if (prev.selectedWorkOrders.length !== next.selectedWorkOrders.length)
    return false;
  for (let i = 0; i < prev.selectedWorkOrders.length; i++) {
    if (prev.selectedWorkOrders[i] !== next.selectedWorkOrders[i]) return false;
  }

  // - Selected work orders order
  if (
    prev.selectedWorkOrdersOrder.length !== next.selectedWorkOrdersOrder.length
  )
    return false;
  for (let i = 0; i < prev.selectedWorkOrdersOrder.length; i++) {
    if (prev.selectedWorkOrdersOrder[i] !== next.selectedWorkOrdersOrder[i])
      return false;
  }

  // - Selected work orders data (compare size and keys)
  if (prev.selectedWorkOrdersData.size !== next.selectedWorkOrdersData.size)
    return false;
  const prevDataKeys = Array.from(prev.selectedWorkOrdersData.keys());
  for (let i = 0; i < prevDataKeys.length; i++) {
    const key = prevDataKeys[i];
    if (!next.selectedWorkOrdersData.has(key)) return false;
  }

  // - Geocoded coordinates used for markers (compare size and keys)
  if (prev.geocodedAddresses.size !== next.geocodedAddresses.size) return false;
  // Avoid direct iteration of MapIterator in older targets
  const prevKeys = Array.from(prev.geocodedAddresses.keys());
  for (let i = 0; i < prevKeys.length; i++) {
    const key = prevKeys[i];
    if (!next.geocodedAddresses.has(key)) return false;
  }

  // - Directions reference (route changes)
  if (prev.directionsResponse !== next.directionsResponse) return false;
  
  // - Optimized directions reference
  if (prev.optimizedDirectionsResponse !== next.optimizedDirectionsResponse) return false;
  if (prev.showOptimizedRoute !== next.showOptimizedRoute) return false;
  
  // - Optimized route order
  if (prev.optimizedRouteOrder?.length !== next.optimizedRouteOrder?.length) return false;
  if (prev.optimizedRouteOrder && next.optimizedRouteOrder) {
    for (let i = 0; i < prev.optimizedRouteOrder.length; i++) {
      if (prev.optimizedRouteOrder[i] !== next.optimizedRouteOrder[i]) return false;
    }
  }

  // - Info window details / selected place
  if (prev.placeDetails !== next.placeDetails) return false;
  if (prev.selectedPlace !== next.selectedPlace) return false;

  // - Starting point changes
  if (prev.startingPoint !== next.startingPoint) return false;
  if (prev.startingPointCoordinates !== next.startingPointCoordinates)
    return false;

  // - Addresses map changes
  if (prev.addresses?.size !== next.addresses?.size) return false;

  // Ignore changes to full workOrders array; map only uses data for selected ids
  // and will re-render when selectedWorkOrders or geocodedAddresses change.

  return true;
}

export default React.memo(LogisticsMapPanel, areEqual);
